import { useLazyQuery, useQuery } from "@apollo/client";
import { TruncateTypography } from "components/TruncateTypography";
import { getFullName } from "encamp-shared/src/utils/name";
import { GetUsersQuery } from "generated-graphql/graphql";
import { uniqBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldValues, Path, PathValue, UseFormReturn } from "react-hook-form";
import { PickerPopoverBase } from "./PickerPopoverBase";
import { GET_USERS_QUERY } from "./userQueries";

type ElementType<V> = V extends (infer U)[] ? U : V;

export type UserPickerPopoverProps<T extends FieldValues, K extends Path<T>> = {
  open: boolean;
  onClose: () => void;
  anchorEl: HTMLElement | null;
  selectedIds: string[];
  form: UseFormReturn<T>;
  allowMultiselect?: boolean;
  formFieldName: K;
  mapValue?: (user: { id: string }) => ElementType<PathValue<T, K>>;
};

type UserItem = NonNullable<
  NonNullable<GetUsersQuery["users"]>["items"]
>[number];

export const UserPickerPopover = <T extends FieldValues, K extends Path<T>>({
  open,
  onClose,
  anchorEl,
  selectedIds,
  allowMultiselect = false,
  form,
  formFieldName,
  mapValue,
}: UserPickerPopoverProps<T, K>) => {
  const PAGE_SIZE = 10;
  const [search, setSearch] = useState("");
  const [loadedItems, setLoadedItems] = useState<UserItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageRef = useRef(0);

  const [selectedUserItems, setSelectedUserItems] = useState<
    { id: string; email: string }[]
  >([]);

  const [loadUsers] = useLazyQuery(GET_USERS_QUERY);

  useEffect(() => {
    if (selectedIds) {
      loadUsers({
        variables: {
          search: `id:${selectedIds.join("|")}`,
          sort: [{ field: "name", sort: "asc" }],
          page: 0,
          pageSize: selectedIds.length,
        },
      }).then(({ data }) => {
        setSelectedUserItems(data?.users?.items ?? []);
      });
    }
  }, [selectedIds, loadUsers]);

  const { data, previousData, loading, fetchMore } = useQuery(GET_USERS_QUERY, {
    variables: {
      search: search ?? "",
      page: 0,
      pageSize: PAGE_SIZE,
      sort: [{ field: "name", sort: "asc" }],
    },
    skip: !open,
    onCompleted: (data) => {
      const items = data?.users?.items ?? [];
      setLoadedItems(items);
      setHasMore(items.length >= PAGE_SIZE);
      pageRef.current = 0;
    },
  });

  // Reset pagination when search changes
  useEffect(() => {
    pageRef.current = 0;
    setLoadedItems([]);
    setHasMore(true);
  }, [search]);

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = pageRef.current + 1;
    pageRef.current = nextPage;

    try {
      const { data: moreData } = await fetchMore({
        variables: {
          search: search ?? "",
          page: nextPage,
          pageSize: PAGE_SIZE,
          sort: [{ field: "name", sort: "asc" }],
        },
      });
      const newItems = moreData?.users?.items ?? [];
      setLoadedItems((prev) => uniqBy([...prev, ...newItems], "id"));
      setHasMore(newItems.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, hasMore, loadingMore, search]);

  const userItems = useMemo(
    () =>
      loadedItems.length > 0
        ? loadedItems
        : data?.users?.items ?? previousData?.users?.items ?? [],
    [loadedItems, data?.users?.items, previousData?.users?.items]
  );

  const allUserItems = useMemo(
    () => uniqBy([...selectedUserItems, ...userItems], "id"),
    [selectedUserItems, userItems]
  );

  const onCloseHandler = useCallback(() => {
    onClose();
    setSearch("");
  }, [onClose]);

  return (
    <PickerPopoverBase
      open={open}
      onClose={onCloseHandler}
      anchorEl={anchorEl}
      search={search}
      onSearchChange={setSearch}
      loading={loading}
      items={allUserItems}
      selectedIds={selectedIds}
      allowMultiselect={allowMultiselect}
      renderPrimary={(item: (typeof userItems)[number]) =>
        getFullName(item.person) || item.email
      }
      renderSecondary={(item: (typeof userItems)[number]) => (
        <TruncateTypography showTooltip variant="body2">
          {getFullName(item.person) ? item.email : undefined}
        </TruncateTypography>
      )}
      emptyLabel="No users found"
      form={form}
      formFieldName={formFieldName}
      mapValue={mapValue}
      onLoadMore={handleLoadMore}
      hasMore={hasMore}
      loadingMore={loadingMore}
    />
  );
};
